import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Home, Hammer, Key, TrendingUp, Sofa, Stethoscope, GraduationCap, Activity,
  Shield, Heart, Receipt, Plus, Trash2, AlertTriangle, CheckCircle2, Info,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Icon map (evita di dover importare dinamicamente)
const ICON_MAP = {
  home: Home,
  hammer: Hammer,
  key: Key,
  "trending-up": TrendingUp,
  sofa: Sofa,
  stethoscope: Stethoscope,
  "graduation-cap": GraduationCap,
  activity: Activity,
  shield: Shield,
  heart: Heart,
};

const CATEGORIA_LABELS = {
  casa: "Casa & Immobili",
  salute: "Salute & Famiglia",
  assicurazioni: "Assicurazioni",
  donazioni: "Donazioni & Erogazioni",
  altro: "Altro",
};

const CATEGORIA_COLORS = {
  casa: "bg-primary/10 text-primary border-primary/20",
  salute: "bg-secondary/10 text-secondary border-secondary/20",
  assicurazioni: "bg-accent text-accent-foreground border-accent",
  donazioni: "bg-primary/10 text-primary border-primary/20",
  altro: "bg-muted",
};

const CURRENT_YEAR = new Date().getFullYear();

export default function Eventi() {
  const [catalog, setCatalog] = useState([]);
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [anno, setAnno] = useState(CURRENT_YEAR);
  const [wizardEvent, setWizardEvent] = useState(null); // catalog item selezionato per il wizard
  const [editingId, setEditingId] = useState(null);
  const [wizardData, setWizardData] = useState({});
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    const [cat, list, rep] = await Promise.all([
      api.get("/eventi/catalog"),
      api.get("/eventi", { params: { anno } }),
      api.get(`/eventi/report/${anno}`),
    ]);
    setCatalog(cat.data.catalog || []);
    setItems(list.data || []);
    setReport(rep.data || null);
  }, [anno]);

  useEffect(() => { loadAll().catch(() => toast.error("Errore caricamento eventi")); }, [loadAll]);

  const byCategoria = useMemo(() => {
    const g = {};
    catalog.forEach((e) => { (g[e.categoria] ||= []).push(e); });
    return g;
  }, [catalog]);

  const openWizard = (event, existing = null) => {
    setWizardEvent(event);
    setEditingId(existing?.id || null);
    setWizardData(existing?.data ? { ...existing.data } : {});
  };

  const closeWizard = () => { setWizardEvent(null); setEditingId(null); setWizardData({}); };

  const saveEvent = async () => {
    if (!wizardEvent) return;
    setSaving(true);
    try {
      const payload = { kind: wizardEvent.id, anno, data: wizardData };
      if (editingId) {
        await api.patch(`/eventi/${editingId}`, payload);
        toast.success("Evento aggiornato");
      } else {
        await api.post("/eventi", payload);
        toast.success("Evento aggiunto — il tuo 730 è stato aggiornato");
      }
      closeWizard();
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Rimuovere questo evento? Non sarà più conteggiato nel calcolo.")) return;
    try {
      await api.delete(`/eventi/${id}`);
      toast.success("Evento rimosso");
      await loadAll();
    } catch { toast.error("Errore rimozione"); }
  };

  const wizardIsValid = useMemo(() => {
    if (!wizardEvent) return false;
    return wizardEvent.wizard.every((f) => {
      if (!f.required) return true;
      const v = wizardData[f.id];
      return v !== undefined && v !== null && v !== "";
    });
  }, [wizardEvent, wizardData]);

  const setField = (id, val) => setWizardData((p) => ({ ...p, [id]: val }));

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8" data-testid="eventi-page">
      {/* Header con selettore anno */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2">Eventi Fiscali</div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl leading-[0.95] mb-2">
            Il tuo 730<br /><span className="text-secondary">in tempo reale.</span>
          </h1>
          <p className="text-muted-foreground max-w-xl">
            Registra mutuo, spese mediche, ristrutturazioni, ONLUS, vendite immobili e altri eventi: l'app calcola
            in automatico l'impatto reale sulla tua dichiarazione.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-muted-foreground">Anno d'imposta</Label>
          <Select value={String(anno)} onValueChange={(v) => setAnno(parseInt(v, 10))}>
            <SelectTrigger className="w-32 rounded-full" data-testid="anno-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Banner forfettario puro (fix C5) */}
      {report?.is_forfettario_puro && (
        <div className="p-4 md:p-5 rounded-2xl border-2 border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="banner-forfettario-puro">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-heading font-bold text-amber-900 mb-1">Sei in regime forfettario puro</div>
            <p className="text-amber-800 leading-relaxed">
              Paghi <b>imposta sostitutiva</b> (5% o 15%) al posto dell'IRPEF: <b>le detrazioni IRPEF (mutuo, mediche, ecc.)
              non sono utilizzabili</b> perché non c'è IRPEF da ridurre. Puoi comunque registrare gli eventi come
              archivio storico — vedrai il valore teorico ma con impatto reale zero. <br />
              <span className="text-xs opacity-80">Restano applicabili le imposte sostitutive (es. plusvalenze) e le deduzioni.</span>
            </p>
          </div>
        </div>
      )}

      {/* Report hero */}
      {report && (
        <Card className="p-6 md:p-8 rounded-3xl border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5" data-testid="report-hero">
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Eventi registrati</div>
              <div className="text-3xl font-heading font-bold" data-testid="report-n-eventi">{report.n_eventi}</div>
              <div className="text-xs text-muted-foreground mt-1">anno {report.anno}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Detrazioni maturate</div>
              <div className="text-3xl font-heading font-bold text-primary" data-testid="report-detrazioni">
                € {report.totale_detrazioni.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-muted-foreground mt-1">riducono l'IRPEF</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Imposte sostitutive</div>
              <div className="text-3xl font-heading font-bold text-secondary" data-testid="report-imposte">
                € {report.totale_imposte_sostitutive.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-muted-foreground mt-1">da versare (es. plusvalenze)</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Impatto netto stimato</div>
              <div className={`text-3xl font-heading font-bold ${report.impatto_netto_stimato >= 0 ? "text-primary" : "text-secondary"}`} data-testid="report-impatto">
                {report.impatto_netto_stimato >= 0 ? "+" : ""}€ {report.impatto_netto_stimato.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {report.impatto_netto_stimato >= 0 ? "risparmio stimato" : "da versare in più"}
              </div>
            </div>
          </div>

          {report.warnings?.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="report-warnings">
              {report.warnings.map((w) => (
                <div key={`w-${w.kind}-${w.message.slice(0, 20)}`} className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">{w.titolo}</div>
                    <div className="text-xs mt-0.5">{w.message}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Eventi già registrati (per categoria) */}
      {items.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-heading font-bold text-2xl">I tuoi eventi {anno}</h2>
          <div className="space-y-3">
            {items.map((it) => {
              const meta = catalog.find((c) => c.id === it.kind) || {};
              const Icon = ICON_MAP[meta.icona] || Info;
              const c = it.calcolo || {};
              return (
                <Card key={it.id} className="p-4 rounded-2xl border flex flex-col md:flex-row md:items-center gap-4" data-testid={`evento-item-${it.id}`}>
                  <div className={`w-11 h-11 rounded-xl ${CATEGORIA_COLORS[meta.categoria] || "bg-muted"} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-base">{meta.titolo || it.kind}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.note || "—"}</div>
                    {meta.rigo_730 && (
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                        Rigo 730: {meta.rigo_730} · Norma: {meta.regola_norma}
                      </div>
                    )}
                  </div>
                  <div className="text-right md:min-w-[140px]">
                    {c.imposta_dovuta > 0 ? (
                      <>
                        <div className="text-xs text-muted-foreground">Imposta dovuta</div>
                        <div className="font-heading font-bold text-lg text-secondary">€ {c.imposta_dovuta.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                      </>
                    ) : c.applicabile === false ? (
                      <>
                        <div className="text-xs text-muted-foreground">Detrazione teorica</div>
                        <div className="font-heading font-bold text-lg text-muted-foreground line-through">€ {(c.detrazione_potenziale || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                        <div className="text-[10px] text-amber-600 uppercase tracking-wider mt-0.5">Non applicabile</div>
                      </>
                    ) : c.detrazione_annua > 0 ? (
                      <>
                        <div className="text-xs text-muted-foreground">Detrazione annua</div>
                        <div className="font-heading font-bold text-lg text-primary">€ {c.detrazione_annua.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</div>
                      </>
                    ) : (
                      <Badge variant="outline" className="text-xs">Nessun impatto</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-full" onClick={() => openWizard(meta, it)} data-testid={`edit-${it.id}`}>
                      Modifica
                    </Button>
                    <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-secondary" onClick={() => deleteEvent(it.id)} data-testid={`delete-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Catalogo eventi */}
      <div className="space-y-6">
        <h2 className="font-heading font-bold text-2xl">Aggiungi un evento</h2>
        {Object.keys(byCategoria).map((cat) => (
          <div key={cat} className="space-y-3" data-testid={`categoria-${cat}`}>
            <h3 className="font-heading font-semibold text-base text-muted-foreground uppercase tracking-wider text-xs">
              {CATEGORIA_LABELS[cat] || cat}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byCategoria[cat].map((e) => {
                const Icon = ICON_MAP[e.icona] || Info;
                return (
                  <Card
                    key={e.id}
                    className="p-5 rounded-2xl border cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-primary/40 transition-all group"
                    onClick={() => openWizard(e)}
                    data-testid={`catalog-item-${e.id}`}
                  >
                    <div className={`w-11 h-11 rounded-xl ${CATEGORIA_COLORS[cat] || "bg-muted"} flex items-center justify-center mb-3`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-heading font-semibold text-sm mb-1 leading-tight">{e.titolo}</div>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">{e.descrizione}</p>
                    <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
                      <span>Rigo {e.rigo_730}</span>
                      <span className="text-primary group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Aggiungi
                      </span>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Wizard modal */}
      <Dialog open={!!wizardEvent} onOpenChange={(o) => { if (!o) closeWizard(); }}>
        <DialogContent className="rounded-2xl max-w-lg" data-testid="wizard-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{wizardEvent?.titolo}</DialogTitle>
            <DialogDescription>{wizardEvent?.descrizione}</DialogDescription>
          </DialogHeader>

          {wizardEvent && (
            <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
              {wizardEvent.wizard.map((f) => (
                <div key={f.id} className="space-y-1.5">
                  <Label htmlFor={`field-${f.id}`} className="text-sm">
                    {f.label}{f.required && <span className="text-secondary"> *</span>}
                  </Label>
                  {f.type === "number" && (
                    <Input
                      id={`field-${f.id}`}
                      type="number"
                      step="0.01"
                      value={wizardData[f.id] ?? f.default ?? ""}
                      onChange={(e) => setField(f.id, e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className="rounded-xl"
                      data-testid={`field-${f.id}`}
                    />
                  )}
                  {f.type === "text" && (
                    <Input
                      id={`field-${f.id}`}
                      value={wizardData[f.id] ?? ""}
                      onChange={(e) => setField(f.id, e.target.value)}
                      className="rounded-xl"
                      data-testid={`field-${f.id}`}
                    />
                  )}
                  {f.type === "boolean" && (
                    <div className="flex items-center gap-3 rounded-xl border p-3">
                      <Switch id={`field-${f.id}`} checked={!!wizardData[f.id]} onCheckedChange={(v) => setField(f.id, v)} data-testid={`field-${f.id}`} />
                      <span className="text-sm text-muted-foreground">{wizardData[f.id] ? "Sì" : "No"}</span>
                    </div>
                  )}
                  {f.type === "select" && (
                    <Select value={wizardData[f.id] ?? ""} onValueChange={(v) => setField(f.id, v)}>
                      <SelectTrigger className="rounded-xl" data-testid={`field-${f.id}`}>
                        <SelectValue placeholder="Seleziona…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
                </div>
              ))}

              {wizardEvent.docs?.length > 0 && (
                <div className="rounded-xl bg-accent/40 p-3 space-y-1">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Documenti utili da conservare</div>
                  <ul className="text-xs text-foreground/80 list-disc list-inside">
                    {wizardEvent.docs.map((d) => <li key={d}>{d}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={closeWizard} className="rounded-full flex-1" data-testid="wizard-cancel">Annulla</Button>
            <Button onClick={saveEvent} disabled={!wizardIsValid || saving} className="rounded-full flex-1" data-testid="wizard-save">
              {saving ? "Salvo…" : editingId ? "Aggiorna" : "Aggiungi al 730"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
